import posthog, { CaptureOptions } from "posthog-js";

import { env } from "@/config/env";

import { getMode } from "./env-mode";

/**
 * PostHog Analytics Utility
 *
 * Handles analytics tracking across different environments:
 * - Development: Only logs to console
 * - Staging: Connects to PostHog with debug enabled
 * - Production: Connects to PostHog with optimized settings
 *
 * All Lotus events include a `project: "lotus"` super property to distinguish
 * them from other Sharp products within the shared PostHog projects.
 *
 * Environment detection uses Vite's MODE (development, staging, production, test).
 */

const isDevelopment = () => {
  const mode = getMode();
  return mode === "development" || mode === "test";
};

const isStaging = () => getMode() === "staging";

const POSTHOG_SUPER_PROPS = { project: "lotus" } as const;

/**
 * Initialize PostHog with analytics and feature flags.
 *
 * Registers `project: "lotus"` as a super property on every event,
 * mirroring the Mixpanel setup so cross-tool reports stay consistent.
 *
 * @example
 * // Called once at app startup in main.tsx
 * initPostHog();
 */
export function initPostHog() {
  if (isDevelopment()) {
    console.log(
      "[PostHog] Running in development mode - analytics and feature flags will use defaults/overrides",
    );
    return;
  }

  const apiKey = env.VITE_POSTHOG_KEY;
  const apiHost = env.VITE_POSTHOG_HOST;

  posthog.init(apiKey, {
    api_host: apiHost,

    autocapture: {
      dom_event_allowlist: ["click"],
      element_allowlist: ["a", "button"],
      css_selector_allowlist: ["[data-ph-capture]"],
    },
    capture_pageview: true,
    capture_pageleave: true,

    persistence: "localStorage",

    debug: isStaging(),

    respect_dnt: true,

    // Session replay starts disabled. The session-replay orchestrator
    // (`initSessionReplay`) turns it on after the vendor flag resolves so
    // PostHog and Sentry Session Replay never run in parallel.
    disable_session_recording: true,
    session_recording: {
      maskAllInputs: false,
      maskTextSelector: "[data-ph-mask]",
      blockSelector: "[data-ph-no-capture]",
    },

    loaded: (ph) => {
      ph.register(POSTHOG_SUPER_PROPS);
      console.log(`[PostHog] Initialized for ${getMode()} environment`);
    },
  });
}

/**
 * Start PostHog Session Replay at runtime.
 *
 * Called by the session-replay orchestrator (`initSessionReplay`) when the
 * vendor flag is on. Pairs with `disable_session_recording: true` in
 * `initPostHog` so recording stays off until this explicit call.
 */
export function enablePostHogSessionReplay() {
  if (isDevelopment()) {
    console.log("[PostHog] Skipping session replay in development");
    return;
  }

  posthog.startSessionRecording();
  console.log("[PostHog] Session replay enabled");
}

/**
 * Identify a user in PostHog
 *
 * Call this after user authentication to associate feature flags with the user.
 *
 * @param userId - Unique identifier for the user
 * @param properties - Optional user properties for targeting
 *
 * @example
 * identifyUser("user-123", { plan: "premium", role: "coach" });
 */
export function identifyUser(
  userId: string,
  properties?: Record<string, unknown>,
) {
  if (isDevelopment()) {
    console.log("[PostHog] Identify User:", userId, properties);
    return;
  }

  posthog.identify(userId, properties);
}

/**
 * Reset PostHog state (useful for logout)
 *
 * Clears the user identity and resets feature flag evaluations.
 */
export function resetPostHog() {
  if (isDevelopment()) {
    console.log("[PostHog] Reset");
    return;
  }

  posthog.reset();
}

/**
 * Register PostHog super properties (attached to every subsequent event).
 *
 * Mirrors `posthog.register(...)` directly. Use this to attach session-level
 * context (e.g. `customer_id`, `entry_point`) so it lands on every captured
 * event without each call site having to thread it through.
 *
 * @param properties - Super properties to register
 *
 * @example
 * registerPostHogSuperProperties({ customer_id: 5392, entry_point: "web" })
 */
export function registerPostHogSuperProperties(
  properties: Record<string, unknown>,
): void {
  if (isDevelopment()) {
    console.log("[PostHog] Register Super Properties:", properties);
    return;
  }

  posthog.register(properties);
}

/**
 * Clear a single PostHog super property so it stops being attached to
 * subsequent events. Counterpart to `registerPostHogSuperProperties` —
 * use when a session transitions from identified to anonymous and a
 * previously-registered value would otherwise leak across visits.
 *
 * @param property - The super property key to clear
 *
 * @example
 * unregisterPostHogSuperProperty("customer_id")
 */
export function unregisterPostHogSuperProperty(property: string): void {
  if (isDevelopment()) {
    console.log("[PostHog] Unregister Super Property:", property);
    return;
  }

  posthog.unregister(property);
}

/**
 * Capture an analytics event in PostHog
 *
 * Use this for custom events beyond autocapture (e.g. business-level actions).
 * Super properties (project: "lotus") are automatically attached to every event.
 *
 * @param eventName - Name of the event (use snake_case by convention)
 * @param properties - Optional key/value properties for the event
 * @param options - Optional PostHog capture options (e.g. transport: "sendBeacon" for unload-time events)
 *
 * @example
 * captureEvent("short_url_visited", { coach_id: "123", source: "email" });
 */
export function captureEvent(
  eventName: string,
  properties?: Record<string, unknown>,
  options?: CaptureOptions,
): void {
  if (isDevelopment()) {
    console.log("[PostHog] Capture Event:", eventName, properties);
    return;
  }

  posthog.capture(eventName, properties, options);
}
